import React, { useState } from 'react';
import { Drawer, Button, Input, message } from 'antd';
import { useMutation } from '@apollo/client';
import { BATCH_SAVE_ADDRESSES, BatchSaveAddressesResponse } from '../graphql/mutations/batchSaveAddresses';
import { HasResource } from '../../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

const { TextArea } = Input;

interface AddStandardizedAddressDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddStandardizedAddressDrawer: React.FC<AddStandardizedAddressDrawerProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [originalAddresses, setOriginalAddresses] = useState<string>('');
  const [parseResults, setParseResults] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const [batchSaveAddresses] = useMutation<BatchSaveAddressesResponse>(BATCH_SAVE_ADDRESSES);

  const handleParseAndSave = async () => {
    if (!originalAddresses) {
      message.warning('请输入原始地址');
      return;
    }

    // 按行分割地址，保留空行，不去除空行
    const addressStrings = originalAddresses.split('\n');

    if (addressStrings.length === 0) {
      message.warning('请输入至少一条地址');
      return;
    }

    setIsProcessing(true);
    try {
      const { data } = await batchSaveAddresses({
        variables: {
          addressStrings,
        },
      });

      if (data?.batchSaveAddresses) {
        const { results, successCount, failedCount } = data.batchSaveAddresses;
        
        // 构建结果显示，不添加数字前缀
        const displayResults = results.map((result) => {
          if (result.errorMessage) {
            return result.errorMessage;
          } else if (result.fullStandardAddress) {
            return result.fullStandardAddress;
          } else {
            return '标准化失败！';
          }
        });
        
        setParseResults(displayResults);
        message.success(`处理完成：成功 ${successCount} 条，失败 ${failedCount} 条`);
        
        // 刷新列表，但不关闭抽屉
        onSuccess();
      }
    } catch (error: any) {
      message.error(`处理失败：${error.message}`);
      console.error('Parse and save addresses error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setOriginalAddresses('');
    setParseResults([]);
    onClose();
  };

  return (
    <Drawer
      title="新增标准地址"
      open={visible}
      onClose={handleClose}
      width={1000}
      destroyOnClose={true}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={handleClose}>取消</Button>
          <HasResource resourceCodes={[ResourceCode.standardizedAddress.BATCH_SAVE]}>
            <Button
              type="primary"
              onClick={handleParseAndSave}
              loading={isProcessing}
              disabled={!originalAddresses.trim()}
            >
              标准化解析&入库
            </Button>
          </HasResource>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-4 h-full">
        {/* 左侧：原始地址输入 */}
        <div className="flex flex-col">
          <h3 className="text-base font-semibold mb-2">原始地址</h3>
          <TextArea
            value={originalAddresses}
            onChange={(e) => setOriginalAddresses(e.target.value)}
            placeholder="请输入原始地址，每行一个地址"
            rows={25}
            className="resize-none flex-1"
          />
        </div>

        {/* 右侧：标准化地址输出 */}
        <div className="flex flex-col">
          <h3 className="text-base font-semibold mb-2">标准化地址</h3>
          <TextArea
            value={parseResults.length > 0
              ? parseResults.join('\n')
              : ''
            }
            placeholder="标准化解析结果"
            readOnly
            rows={25}
            className="resize-none flex-1 bg-gray-50"
          />
        </div>
      </div>
    </Drawer>
  );
};

export default AddStandardizedAddressDrawer;

